import { useState, useEffect, useContext } from 'react';
import { AuthContext } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import { Share2, Copy, Check, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Invite = () => {
  const { user, token } = useContext(AuthContext);
  const [inviteCode, setInviteCode] = useState('');
  const [inviteLink, setInviteLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [referrals, setReferrals] = useState([]);

  useEffect(() => {
    fetchInviteCode();
    fetchReferrals();
  }, []);

  const fetchInviteCode = async () => {
    try {
      const response = await axios.get(`${API}/invite/code`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInviteCode(response.data.invite_code);
      // Use the actual frontend URL from the browser
      const frontendUrl = window.location.origin;
      const link = `${frontendUrl}/auth?ref=${response.data.invite_code}`;
      setInviteLink(link);
    } catch (error) {
      console.error('Failed to fetch invite code');
      toast.error('Failed to load invite code');
    }
  };

  const fetchReferrals = async () => {
    try {
      const response = await axios.get(`${API}/invite/referrals`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReferrals(response.data);
    } catch (error) {
      console.error('Failed to fetch referrals');
    }
  };

  const copyToClipboard = () => {
    // Fallback copy method that works in more environments
    const textArea = document.createElement('textarea');
    textArea.value = inviteLink;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand('copy');
      setCopied(true);
      toast.success('Invite link copied to clipboard!');
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      toast.error('Failed to copy link');
    } finally {
      document.body.removeChild(textArea);
    }
  };

  const shareViaWhatsApp = () => {
    const message = `Hey! Join me on CultraMeet - Connect with amazing people worldwide! 🌍\n\n${inviteLink}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const shareViaFacebook = () => {
    // Copy link to clipboard using fallback method
    const textArea = document.createElement('textarea');
    textArea.value = inviteLink;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand('copy');
      toast.info('Link copied! Open Facebook and paste it in a post or message', {
        duration: 5000
      });
    } catch (err) {
      toast.error('Failed to copy link. Please copy manually from the input field above.');
    } finally {
      document.body.removeChild(textArea);
    }
  };

  const shareViaNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join CultraMeet',
          text: 'Connect with amazing people worldwide on CultraMeet!',
          url: inviteLink
        });
        toast.success('Shared successfully!');
      } catch (error) {
        // User cancelled or share failed
        if (error.name !== 'AbortError') {
          // If not cancelled by user, fall back to copy
          const textArea = document.createElement('textarea');
          textArea.value = inviteLink;
          textArea.style.position = 'fixed';
          textArea.style.left = '-999999px';
          document.body.appendChild(textArea);
          textArea.select();
          try {
            document.execCommand('copy');
            toast.info('Link copied to clipboard! Share it anywhere you like.');
          } catch (copyErr) {
            toast.error('Failed to share. Please copy the link manually.');
          } finally {
            document.body.removeChild(textArea);
          }
        }
      }
    } else {
      // Fallback for browsers without share API
      const textArea = document.createElement('textarea');
      textArea.value = inviteLink;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        toast.info('Link copied to clipboard! Share it anywhere you like.');
      } catch (err) {
        toast.error('Sharing not supported. Please copy the link manually from above.');
      } finally {
        document.body.removeChild(textArea);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-peach-50 to-teal-50">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 py-8 pt-24">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <div className="bg-white border-2 border-teal p-4 rounded-full">
              <Users className="w-12 h-12 text-black" />
            </div>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">Invite Friends</h1>
          <p className="text-gray-600 text-lg">
            Share CultraMeet with your friends and grow our community!
          </p>
        </div>

        {/* Invite Link Section */}
        <div className="glass-dark rounded-3xl p-8 mb-6">
          <h2 className="text-2xl font-bold mb-4">Your Invite Link</h2>
          <p className="text-gray-600 mb-4">
            Share this link with friends to invite them to join CultraMeet
          </p>
          
          <div className="flex space-x-2 mb-6">
            <Input
              value={inviteLink}
              readOnly
              className="flex-1 py-6 rounded-xl bg-white"
            />
            <Button
              onClick={copyToClipboard}
              className="bg-white text-black border-2 border-teal hover:bg-teal hover:text-white rounded-xl px-6"
            >
              {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
            </Button>
          </div>

          {/* Social Media Share Buttons */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* WhatsApp */}
            <Button
              onClick={shareViaWhatsApp}
              className="bg-[#25D366] hover:bg-[#1da851] text-white rounded-xl py-6 font-semibold"
            >
              <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
              WhatsApp
            </Button>

            {/* Facebook */}
            <Button
              onClick={shareViaFacebook}
              className="bg-[#1877F2] hover:bg-[#0c5fc7] text-white rounded-xl py-6 font-semibold"
            >
              <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
              Facebook
            </Button>

            {/* Instagram (Copy Link) */}
            <Button
              onClick={() => {
                // Copy using fallback method
                const textArea = document.createElement('textarea');
                textArea.value = inviteLink;
                textArea.style.position = 'fixed';
                textArea.style.left = '-999999px';
                document.body.appendChild(textArea);
                textArea.select();
                try {
                  document.execCommand('copy');
                  toast.info('Link copied! Share it on Instagram');
                } catch (err) {
                  toast.error('Failed to copy link');
                } finally {
                  document.body.removeChild(textArea);
                }
              }}
              className="bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#F77737] hover:opacity-90 text-white rounded-xl py-6 font-semibold"
            >
              <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
              Instagram
            </Button>

            {/* TikTok (Copy Link) */}
            <Button
              onClick={() => {
                // Copy using fallback method
                const textArea = document.createElement('textarea');
                textArea.value = inviteLink;
                textArea.style.position = 'fixed';
                textArea.style.left = '-999999px';
                document.body.appendChild(textArea);
                textArea.select();
                try {
                  document.execCommand('copy');
                  toast.info('Link copied! Share it on TikTok');
                } catch (err) {
                  toast.error('Failed to copy link');
                } finally {
                  document.body.removeChild(textArea);
                }
              }}
              className="bg-black hover:bg-gray-800 text-white rounded-xl py-6 font-semibold"
            >
              <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
              </svg>
              TikTok
            </Button>
          </div>

          {/* Native Share Button */}
          <Button
            onClick={shareViaNative}
            className="w-full mt-4 bg-white text-black border-2 border-teal hover:bg-teal hover:text-white rounded-xl py-6 font-semibold"
          >
            <Share2 className="w-5 h-5 mr-2" />
            Share via...
          </Button>
        </div>

        {/* Referrals Stats */}
        <div className="glass-dark rounded-3xl p-8">
          <h2 className="text-2xl font-bold mb-4">Your Referrals</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="bg-white border-2 border-teal rounded-2xl p-6 text-center">
              <p className="text-4xl font-bold mb-2 text-black">{referrals.length}</p>
              <p className="text-sm font-semibold text-black">Friends Invited</p>
            </div>
            <div className="bg-white border-2 border-teal rounded-2xl p-6 text-center">
              <p className="text-4xl font-bold mb-2 text-black">{inviteCode}</p>
              <p className="text-sm font-semibold text-black">Your Invite Code</p>
            </div>
            <div className="bg-white border-2 border-teal rounded-2xl p-6 text-center">
              <p className="text-4xl font-bold mb-2">🎉</p>
              <p className="text-sm font-semibold text-black">Keep Inviting!</p>
            </div>
          </div>

          {referrals.length > 0 && (
            <div>
              <h3 className="font-semibold mb-3">Recent Referrals</h3>
              <div className="space-y-2">
                {referrals.map((referral, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-xl p-4 flex items-center justify-between"
                  >
                    <div>
                      <p className="font-semibold">{referral.name}</p>
                      <p className="text-sm text-gray-500">
                        Joined {new Date(referral.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="bg-teal-50 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold">
                      Referred
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Invite;
